// AdvancedResultsSummary.tsx
import * as React from "react";
import { Chip, Menu, MenuItem } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useSearchParams } from "react-router-dom";
import { useLSSearch } from "../ls/LSSearch";
import {
  AdvancedSearchTerm,
  ReadingLevelSearchTerm,
} from "./AdvancedSearchTerm";
import { advancedSearchTermAdapter } from "./advancedSearchTermAdapter";
import { EditAdvancedSearchButton } from "./EditAdvancedSearchButton";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { readingLevelSearchAdapter } from "./reading-levels/readingLevelSearchAdapter";
import { isNonEmptySearchToken } from "./SearchToken";
import { getReadingLevelToken } from "@tlc/ls2pac/components/ReadingLevelSearchForm";
import { SearchFieldTranslateKey } from "./SearchFieldKey";
import { SearchTypeTranslateKey } from "./SearchTypeKey";

export function AdvancedSearchTokenDisplay() {
  const [_, setSearchParams] = useSearchParams();
  const { searchParams: advancedParams } = useLSSearch();
  const { translate } = useLanguage();
  const rawTerm = advancedParams.rawTerm as AdvancedSearchTerm;

  const updateTerm = (next: AdvancedSearchTerm) => {
    // write keeps all other params (audience, branchFilters, facetFilters, etc.)
    setSearchParams(
      (params) => {
        advancedSearchTermAdapter.setTerm(params, next);
        params.delete("page");
        return params;
      },
      {
        replace: false,
      }
    );
  };

  const removeTokenAt = (idx: number) => {
    const next = {
      ...rawTerm,
      searchTokens: rawTerm.searchTokens.filter((_, i) => i !== idx),
    };
    // if there are no tokens, the loader should redirect to the search form automatically
    updateTerm(next);
  };

  const setJoin = (isAnd: boolean) => {
    updateTerm({ ...rawTerm, isAnd });
  };

  return (
    <React.Fragment>
      {/* token chips */}
      {rawTerm.searchTokens.flatMap((t, i) => {
        const tokenEl = (
          <Chip
            sx={{
              maxWidth: "320px",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
            key={`tok-${t.field}|${t.type}|${t.searchString}|${i}`}
            variant="outlined"
            color="secondary"
            onDelete={() => removeTokenAt(i)}
            label={`${translate(SearchFieldTranslateKey[t.field])} ${translate(
              SearchTypeTranslateKey[t.type]
            )} "${t.searchString}"`}
          />
        );

        // only one join chip after the first token
        if (rawTerm.searchTokens.length > 1 && i === 0) {
          return [
            tokenEl,
            <JoinChip key={`join`} value={rawTerm.isAnd} onChange={setJoin} />,
          ];
        }

        return [tokenEl];
      })}
      <EditAdvancedSearchButton />
    </React.Fragment>
  );
}

export function JoinChip({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (isAnd: boolean) => void;
}) {
  const { translate } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const andOrOpen = Boolean(anchorEl);

  const openJoinMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const closeJoinMenu = () => setAnchorEl(null);
  return (
    <>
      <Chip
        variant="outlined"
        color="secondary"
        onClick={openJoinMenu}
        onDelete={openJoinMenu}
        label={value ? translate("and") : translate("or").toUpperCase()}
        deleteIcon={<ArrowDropDownIcon />}
      />
      <Menu anchorEl={anchorEl} open={andOrOpen} onClose={closeJoinMenu}>
        <MenuItem
          selected={value}
          onClick={() => {
            onChange(true);
            closeJoinMenu();
          }}
        >
          {translate("and")}
        </MenuItem>
        <MenuItem
          selected={!value}
          onClick={() => {
            onChange(false);
            closeJoinMenu();
          }}
        >
          {translate("or").toUpperCase()}
        </MenuItem>
      </Menu>
    </>
  );
}

export function ReadingLevelTokenDisplay() {
  const { translate } = useLanguage();
  const [_, setSearchParams] = useSearchParams();
  const { searchParams: advancedParams } = useLSSearch();
  const rawTerm = advancedParams.rawTerm as ReadingLevelSearchTerm;
  const { readingLevelFilters } = rawTerm;
  const {
    interestLevel,
    lexileEnd,
    lexileStart,
    bookLevelEnd,
    bookLevelStart,
  } = readingLevelFilters;

  const updateTerm = (next: ReadingLevelSearchTerm) => {
    // write keeps all other params (audience, branchFilters, facetFilters, etc.)
    setSearchParams(
      (params) => {
        readingLevelSearchAdapter.setTerm(params, next);
        params.delete("page");
        return params;
      },
      {
        replace: false,
      }
    );
  };

  const clearInterestLevel = () => {
    const next: ReadingLevelSearchTerm = {
      ...rawTerm,
      readingLevelFilters: { ...readingLevelFilters, interestLevel: null },
    };
    updateTerm(next);
  };

  const clearARRange = () => {
    const next: ReadingLevelSearchTerm = {
      ...rawTerm,
      readingLevelFilters: {
        ...readingLevelFilters,
        bookLevelEnd: null,
        bookLevelStart: null,
      },
    };
    updateTerm(next);
  };

  const clearLexileRange = () => {
    const next: ReadingLevelSearchTerm = {
      ...rawTerm,
      readingLevelFilters: {
        ...readingLevelFilters,
        lexileEnd: null,
        lexileStart: null,
      },
    };
    updateTerm(next);
  };

  const clearSearchTerm = () => {
    const next = { ...rawTerm, searchTokens: [getReadingLevelToken("")] };
    updateTerm(next);
  };

  const searchTerm = React.useMemo(() => {
    const token = rawTerm.searchTokens?.[0];
    if (isNonEmptySearchToken(token)) return token.searchString;
    return null;
  }, [rawTerm]);

  return (
    <React.Fragment>
      {searchTerm && (
        <Chip
          sx={{
            maxWidth: "320px",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
          variant="outlined"
          color="secondary"
          onDelete={() => clearSearchTerm()}
          label={searchTerm}
        />
      )}
      {(bookLevelEnd || bookLevelStart) && (
        <Chip
          sx={{
            maxWidth: "320px",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
          variant="outlined"
          color="secondary"
          onDelete={() => clearARRange()}
          label={
            <>
              {translate("atosLevel")}: {bookLevelStart}
              {bookLevelEnd != bookLevelStart && <> - {bookLevelEnd}</>}
            </>
          }
        />
      )}
      {interestLevel && (
        <Chip
          sx={{
            maxWidth: "320px",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
          variant="outlined"
          color="secondary"
          onDelete={() => clearInterestLevel()}
          label={
            <>
              {translate("interestLevel")}: {interestLevel}
            </>
          }
        />
      )}
      {(lexileStart || lexileEnd) && (
        <Chip
          sx={{
            maxWidth: "320px",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
          variant="outlined"
          color="secondary"
          onDelete={() => clearLexileRange()}
          label={
            <>
              {translate("lexileMeasure")}: {lexileStart}
              {lexileEnd != lexileStart && <> - {lexileEnd}</>}
            </>
          }
        />
      )}
      <EditAdvancedSearchButton />
    </React.Fragment>
  );
}
